import { get, patch, post } from 'utils/network';
import { ApiRoute } from '../../utils/config';
import { SignupCredentials, User } from '../../models/user.types';

// Fetch current user
export const fetchCurrentUser = async (isLoggedIn: boolean): Promise<User | undefined> => {
  // return a mock User profile for prospect users
  if (!isLoggedIn) {
    return {
      email: '',
      firstName: '',
      lastName: '',
      zipCode: '',
      phoneNumber: '',
      plan: '',
      hasValidPaymentMethod: true,
      id: 0,
      isAdmin: false,
      isEmployee: false,
      status: 'active',
      unsentGiveawaysCount: 0,
    } as User;
  }

  const {
    ok,
    parsedBody,
  } = await get<User>(`${ApiRoute.CURRENT_USER}`);
  if (!ok) {
    throw new Error('Network response was not ok');
  }

  return parsedBody;
};

export const reactivateSubscription = async (): Promise<void> => {
  const body = {
    activationAction: 'reactivate',
  };

  const { ok } = await patch<void>(`${ApiRoute.CURRENT_SUBSCRIPTION}`, body);
  if (!ok) {
    throw new Error('Network response was not ok');
  }
};

export const pauseSubscription = async (activateAfterWeeks: number): Promise<void> => {
  const body = {
    activationAction: 'deactivate',
    subscription: {
      status: 'returning',
      activateAfterWeeks,
    },
  };

  const { ok } = await patch<void>(`${ApiRoute.CURRENT_SUBSCRIPTION}`, body);
  if (!ok) {
    throw new Error('Network response was not ok');
  }
};

export const cancelSubscription = async (reason: string, reasonDetails: string,
  preparedFollowUpOptIn: boolean): Promise<void> => {
  const body = {
    activationAction: 'deactivate',
    subscription: {
      status: 'cancelled',
      subscriptionCancellationAttributes: {
        reason,
        reasonDetails,
        preparedFollowUpOptIn,
      },
    },
  };

  const { ok } = await patch<void>(`${ApiRoute.CURRENT_SUBSCRIPTION}`, body);
  if (!ok) {
    throw new Error('Network response was not ok');
  }
};

export const createAccount = async (credentials: SignupCredentials): Promise<string | null> => {
  const body = {
    user: credentials,
    plan: 'chefs_choice',
    extras: 0,
    is_diversified: true,
  };

  const response = await post<void>(`${ApiRoute.CREATE_ACCOUNT}`, body);
  if (!response.ok) {
    throw new Error('Network response was not ok');
  }

  return response.headers.get('signup_status');
};

export const activateSubscription = async (couponCode?: string): Promise<void> => {
  const body = {
    subscription_agreement: 'true',
    ...(couponCode ? { coupon_code: couponCode } : {}),
  };

  const response = await patch<{ details?: string }>(`${ApiRoute.ACTIVATE}`, body);
  if (!response.ok) {
    if (response.parsedBody?.details) {
      Rollbar.error(response.parsedBody?.details);
    }
    throw new Error(response.parsedBody?.details || 'There was an issue activating the subscription.');
  }
};

export const loginExistingAccount = async (formData: FormData): Promise<string | null> => {
  const currentParams = new URLSearchParams(window.location.search).toString();
  const url = currentParams ? `${ApiRoute.LOGIN_ACCOUNT}&${currentParams}` : ApiRoute.LOGIN_ACCOUNT;
  const response = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    redirect: 'follow',
    body: formData,
  });

  if (!response.ok) {
    throw new Error('Network response was not ok');
  }

  return response.headers.get('sign_in_status');
};
